// SPDX-License-Identifier: MIT
// Platform resolver. Tauri injects `window.__TAURI_INTERNALS__` (Tauri 2) only
// in the desktop shell ; the web build never has it. Resolved once.
import type { AiBridge, Platform } from './types';
import type { DataBridge } from './data-types';
import type { AttachmentBridge } from './attachment-types';
import { webAiBridge } from './web-bridge';
import { desktopAiBridge } from './desktop-bridge';
import { webDataBridge } from './web-data-bridge';
import { desktopDataBridge } from './desktop-data-bridge';
import { webAttachmentBridge } from './web-attachment-bridge';
import { desktopAttachmentBridge } from './desktop-attachment-bridge';
import { createDesktopUsageBridge, createWebUsageBridge, type UsageBridge } from './usage-bridge';
import { createUsageStore } from './usage-store';
import { lastWebUsageEvent } from './web-ratelimit-cache';
import { softUpsellUnlocked } from './usage-adaptive';
import { REPEAT_WINDOW_DAYS } from './usage-types';

export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'web';
  const w = window as unknown as Record<string, unknown>;
  return w.__TAURI_INTERNALS__ !== undefined || w.__TAURI__ !== undefined ? 'desktop' : 'web';
}

export function getAiBridge(): AiBridge {
  return detectPlatform() === 'desktop' ? desktopAiBridge : webAiBridge;
}

export function getDataBridge(): DataBridge {
  return detectPlatform() === 'desktop' ? desktopDataBridge : webDataBridge;
}

export function getAttachmentBridge(): AttachmentBridge {
  return detectPlatform() === 'desktop' ? desktopAttachmentBridge : webAttachmentBridge;
}

let _usageStore: ReturnType<typeof createUsageStore> | null = null;
function usageStore(): ReturnType<typeof createUsageStore> {
  if (!_usageStore) _usageStore = createUsageStore(window.localStorage);
  return _usageStore;
}

// §5 consent accessors for the gauge UI (desktop). Reads of ~/.claude/projects
// are gated on this; the gauge still works stream-only (status/reset) without it.
export function usageConsentGranted(): boolean { return usageStore().hasConsent(); }
export function grantUsageConsent(): void { usageStore().grantConsent(); }

// Soft-upsell gate : true once the user hit the wall REPEAT_THROTTLES+ times in
// the trailing window (cf usage-adaptive.softUpsellUnlocked). Never on first hit.
export function usageThrottleRepeated(): boolean {
  const sinceMs = Date.now() - REPEAT_WINDOW_DAYS * 86_400_000;
  return softUpsellUnlocked(usageStore().throttleCountSince(sinceMs));
}

let _usageBridge: UsageBridge | null = null;
export function getUsageBridge(): UsageBridge {
  if (_usageBridge) return _usageBridge;
  const now = () => Date.now();
  if (detectPlatform() === 'desktop') {
    const store = usageStore();
    _usageBridge = createDesktopUsageBridge({
      // Dynamic import keeps @tauri-apps/api out of the web bundle entirely.
      invoke: (cmd, args) => import('@tauri-apps/api/core').then((m) => m.invoke(cmd as string, args as Record<string, unknown>)),
      store,
      now,
      consent: () => store.hasConsent(),
      // Source autoritative : `claude -p "/usage"` via la commande Rust claude_usage.
      usageCommand: () => import('@tauri-apps/api/core')
        .then((m) => m.invoke('claude_usage') as Promise<string>)
        .catch(() => null),
      // Type de forfait : `claude auth status` → subscriptionType (one-shot, caché par le bridge).
      subscriptionType: () => import('@tauri-apps/api/core')
        .then((m) => m.invoke('claude_auth_status') as Promise<string>)
        .then((s) => { try { return (JSON.parse(s).subscriptionType as string) ?? null; } catch { return null; } })
        .catch(() => null),
    });
  } else {
    _usageBridge = createWebUsageBridge(lastWebUsageEvent, now);
  }
  return _usageBridge;
}

export type { AiBridge, Platform, DataBridge, AttachmentBridge, UsageBridge };
